import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TArticle } from '../../../models/common';
import ArticleRow from '../../organisms/article-row/article-row';
import ArticleContent from '../../organisms/article-content/article-content';
import ArticleCard from '../../organisms/article-card/article-card';
import useLike from 'custom-hooks/useLike';
import useComment from 'custom-hooks/useComment';
import ConfirmDialog from 'components/organisms/confirm-dialog/confirm-dialog';

export function WArticle({
  type,
  data,
  typeContentClass,
  typeRowClass,
  onDelete,
  hideInteractions,
}: {
  type: 'card' | 'row' | 'content';
  data: TArticle;
  typeContentClass?: string;
  typeRowClass?: string;
  onDelete?: (id: string) => void;
  hideInteractions?: boolean;
}): JSX.Element {
  const { totalCommentCount, lastComment, handlePostChanged } = useComment(data._id!);
  const { isLiked, likesMiniData, totalLikeCount, handleLike } = useLike(data);

  const [showComments, setShowComments] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [candidateForDelete, setCandidateForDelete] = useState('');

  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToArticle = (article_id: string) => {
    navigate(article_id);
  };

  const handleClickLike = () => {
    handleLike();
  };

  const handleClickDeleteArticle = (articleId: string) => {
    setCandidateForDelete(articleId);
  };

  const handleClickConfirmDelete = () => {
    onDelete!(candidateForDelete);
    setCandidateForDelete('');
  };

  const articleTypes = {
    card: (
      <ArticleCard
        data={data}
        anchorEl={anchorEl}
        open={open}
        isLiked={isLiked}
        totalCommentCount={totalCommentCount}
        totalLikeCount={totalLikeCount}
        handleClick={handleClick}
        handleClose={handleClose}
        navigateToArticle={navigateToArticle}
        onLikeClick={handleClickLike}
        deleteArticle={handleClickDeleteArticle}
        hideInteractions={hideInteractions}
      />
    ),
    content: (
      <ArticleContent
        data={data}
        anchorEl={anchorEl}
        open={open}
        isLiked={isLiked}
        totalLikeCount={totalLikeCount}
        totalCommentCount={totalCommentCount}
        likesMini={likesMiniData}
        handleClick={handleClick}
        handleClose={handleClose}
        showComments={showComments}
        setShowComments={setShowComments}
        onLikeClick={handleClickLike}
        deleteArticle={handleClickDeleteArticle}
        onCommentedChanged={handlePostChanged}
        className={typeContentClass}
        hideInteractions={hideInteractions}
      />
    ),
    row: (
      <ArticleRow
        data={data}
        anchorEl={anchorEl}
        open={open}
        isLiked={isLiked}
        totalLikeCount={totalLikeCount}
        totalCommentCount={totalCommentCount}
        lastComment={lastComment}
        handleClick={handleClick}
        handleClose={handleClose}
        navigateToArticle={navigateToArticle}
        onLikeClick={handleClickLike}
        deleteArticle={handleClickDeleteArticle}
        className={typeRowClass}
        hideInteractions={hideInteractions}
      />
    ),
  };

  return (
    <>
      {articleTypes[type]}

      <ConfirmDialog
        open={!!candidateForDelete}
        setOpen={() => setCandidateForDelete('')}
        onConfirm={handleClickConfirmDelete}
        title="Delete Article"
        text="This action will permanently delete your article, do you wish to delete your article?"
        confirmText="Delete"
        isLeaveAction={false}
      />
    </>
  );
}
